import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../components/context/AuthContext';
import { useToast } from './context/ToastContext';
import useInstallPrompt from '../hooks/useInstallPrompt';
import Icon from '../components/AppIcon';
import Button from './ui/Button';
import AppLogo from './AppLogo';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const { addToast } = useToast();
  const { installPromptEvent, isInstallable, isInstalled, promptInstall, dismissInstallPrompt } = useInstallPrompt();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-primary/10 rounded-full">
            <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">Loading...</p>
          <p className="text-sm text-muted-foreground font-caption mt-1">Please wait</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login and save the attempted location
  if (!isAuthenticated) {
    return <Navigate to="/admin/auth" state={{ from: location }} replace />;
  }

  const acceptedRoles = (Array.isArray(requiredRole) ? requiredRole : [requiredRole])
    .filter(Boolean)
    .map((role) => String(role).toLowerCase());
  const hasRequiredRole = acceptedRoles.length === 0 || user?.roles?.some(
    (role) => acceptedRoles.includes(String(role).toLowerCase())
  );

  if (!hasRequiredRole) {
    return <Navigate to="/my-tasks" replace />;
  }

  const handleInstallClick = async () => {
    if (!installPromptEvent) {
      addToast('Install prompt is only available in a supported browser context. You can still use your browser’s “Install app” menu.', 'info');
      return;
    }

    const accepted = await promptInstall();
    if (!accepted) {
      addToast('The install prompt was not completed in this browser context.', 'info');
    }
  };

  // If authenticated, render the protected content
  return (
    <>
      {children}
      {isInstallable && !isInstalled && (
        <div className="fixed bottom-4 right-4 z-[60] w-[min(92vw,380px)] rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur-md">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-secondary text-white shadow-lg shadow-primary/20">
              <AppLogo variant="icon" size={28} color="#FFFFFF" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-lg font-bold text-foreground">Install Claw</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Keep your workspace at your fingertips from your home screen.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" size="sm" onClick={dismissInstallPrompt} className="w-full sm:w-auto">
              Dismiss
            </Button>
            <Button size="sm" onClick={handleInstallClick} className="w-full sm:w-auto">
              Install
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProtectedRoute;
